import React, { useState } from 'react';
import { Save, Image, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../context/BrandContext';
import fileUploadService from '../../services/fileUploadService';

const BrandSettings = () => {
  const { t } = useTranslation();
  const { brand, updateBrandSettings } = useBrand();
  const [formData, setFormData] = useState({
    name: brand.name || '',
    logoUrl: brand.logoUrl || '',
    faviconUrl: brand.faviconUrl || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, favicon: false });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error'); // 'success' or 'error'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    try {
      setUploading(prev => ({ ...prev, [type]: true }));
      setMessage('');

      let result;
      if (type === 'logo') {
        result = await fileUploadService.uploadLogo(file);
      } else if (type === 'favicon') {
        result = await fileUploadService.uploadFavicon(file);
      }

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          [`${type}Url`]: result.data.url
        }));
        setMessage(`${t(`admin.brandSettings.${type}Uploaded`)}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(result.error || `${t('admin.brandSettings.uploadFailed')} ${type}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage(`${t('admin.brandSettings.uploadFailed')} ${type}`);
      setMessageType('error');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setMessage(t('admin.brandSettings.brandNameRequired'));
      setMessageType('error');
      return;
    }

    try {
      setSaving(true);
      setMessage('');
      
      const result = await updateBrandSettings(formData);
      
      if (result.success) {
        setMessage(t('admin.brandSettings.settingsUpdated'));
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(result.error || t('admin.brandSettings.updateFailed'));
        setMessageType('error');
      }
    } catch (error) {
      setMessage(t('admin.brandSettings.updateFailed'));
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    setFormData({
      name: brand.name || '',
      logoUrl: brand.logoUrl || '',
      faviconUrl: brand.faviconUrl || ''
    });
  }, [brand]);

  const FileUploadSection = ({ type, label, description, currentUrl, accept = "image/*" }) => (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <label className="cursor-pointer bg-[#374151] hover:bg-[#4b5563] border border-gray-600 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium text-white transition-colors">
            <Upload className="w-4 h-4" />
            {uploading[type] ? t('admin.brandSettings.uploading') : (type === 'logo' ? t('admin.brandSettings.chooseLogo') : t('admin.brandSettings.chooseFavicon'))}
            <input
              type="file"
              accept={accept}
              onChange={(e) => handleFileUpload(e.target.files[0], type)}
              className="hidden"
              disabled={uploading[type]}
            />
          </label>
          
          {uploading[type] && (
            <div className="flex items-center gap-2 text-[#a15507]">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#a15507] border-t-transparent"></div>
              <span className="text-sm">{t('admin.brandSettings.uploading')}</span>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400">{description}</p>
        
        {currentUrl && (
          <div className="flex items-center gap-3 p-3 bg-[#374151] rounded-lg border border-gray-600">
            <span className="text-xs text-gray-400">{t('admin.brandSettings.current')}:</span>
            <img 
              src={currentUrl} 
              alt={`${label} preview`} 
              className={`object-contain ${type === 'favicon' ? 'h-4 w-4' : 'h-8 w-auto max-w-32'}`}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="text-xs text-gray-400 break-all">{currentUrl.split('/').pop()}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-[#1f2937] rounded-lg shadow-lg border border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Image className="w-6 h-6 text-[#a15507]" />
        <h2 className="text-xl font-semibold text-white">{t('admin.brandSettings.title')}</h2>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm border ${
          messageType === 'success'
            ? 'bg-green-900/50 text-green-300 border-green-700' 
            : 'bg-red-900/50 text-red-300 border-red-700'
        }`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('admin.brandSettings.brandName')}
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-3 py-2 bg-[#374151] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-[#a15507] focus:border-transparent"
            placeholder={t('admin.brandSettings.brandNamePlaceholder')}
            required
          />
          <p className="text-xs text-gray-400 mt-1">
            {t('admin.brandSettings.brandNameDescription')}
          </p>
        </div>

        <FileUploadSection
          type="logo"
          label={t('admin.brandSettings.brandLogo')}
          description={t('admin.brandSettings.logoDescription')}
          currentUrl={formData.logoUrl}
        />

        <FileUploadSection
          type="favicon"
          label={t('admin.brandSettings.favicon')}
          description={t('admin.brandSettings.faviconDescription')}
          currentUrl={formData.faviconUrl}
        />

        <div className="flex items-center justify-between pt-4 border-t border-gray-700">
          <p className="text-sm text-gray-400">
            {t('admin.brandSettings.changesApplyNote')}
          </p>
          <button
            type="submit"
            disabled={saving || uploading.logo || uploading.favicon}
            className="bg-[#a15507] hover:bg-[#924e06] disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? t('admin.brandSettings.saving') : t('admin.brandSettings.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BrandSettings;
